import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { aiAPI } from '@/utils/api';
import { Loader2, Send, Sparkles, Factory, Beaker, Package, BookOpen, CheckCircle2, AlertCircle, ArrowRight, RefreshCw, CalendarPlus, Paperclip, FileText, Check, X, Crown, Zap } from 'lucide-react';
import { toast } from 'sonner';

const ACTION_ICON_MAP = {
  create_material: { icon: Package, color: 'text-blue-600 bg-blue-50', label: 'Matière créée' },
  create_finished_product: { icon: Factory, color: 'text-emerald-600 bg-emerald-50', label: 'Produit créé' },
  add_recipe_line: { icon: BookOpen, color: 'text-amber-600 bg-amber-50', label: 'Recette' },
  update_recipe_line: { icon: BookOpen, color: 'text-orange-600 bg-orange-50', label: 'Recette modifiée' },
  create_journal_entry: { icon: BookOpen, color: 'text-purple-600 bg-purple-50', label: 'Journal' },
  add_stock: { icon: Package, color: 'text-green-600 bg-green-50', label: 'Stock ajouté' },
  update_material: { icon: RefreshCw, color: 'text-cyan-600 bg-cyan-50', label: 'Matière modifiée' },
  update_finished_product: { icon: RefreshCw, color: 'text-teal-600 bg-teal-50', label: 'Produit modifié' },
  add_simulation: { icon: CalendarPlus, color: 'text-indigo-600 bg-indigo-50', label: 'Prévisionnel' },
  error: { icon: AlertCircle, color: 'text-red-600 bg-red-50', label: 'Erreur' },
};

const ActionBadge = ({ action }) => {
  const config = ACTION_ICON_MAP[action.type] || ACTION_ICON_MAP.error;
  const Icon = config.icon;

  let detail = '';
  if (action.type === 'create_material') detail = `${action.name} (${action.category || ''})`;
  else if (action.type === 'create_finished_product') detail = action.name;
  else if (action.type === 'add_recipe_line' || action.type === 'update_recipe_line') detail = `${action.material} (${action.quantity}) → ${action.product}`;
  else if (action.type === 'create_journal_entry') detail = `${action.product} × ${action.quantity} le ${action.date}`;
  else if (action.type === 'add_stock') detail = `${action.name}: +${action.added}${action.unit} (${action.old_stock} → ${action.new_stock}${action.unit})`;
  else if (action.type === 'update_material') detail = `${action.name} → ${(action.fields || []).join(', ')}`;
  else if (action.type === 'update_finished_product') detail = `${action.name} → ${(action.fields || []).join(', ')}`;
  else if (action.type === 'add_simulation') detail = `${action.product} × ${action.units} lots planifiés`;
  else if (action.type === 'error') detail = action.message;

  return (
    <div data-testid={`action-badge-${action.type}`} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${config.color}`}>
      <Icon size={15} className="shrink-0" />
      <span className="font-medium shrink-0">{config.label}</span>
      <ArrowRight size={12} className="shrink-0" />
      <span className="truncate">{detail}</span>
    </div>
  );
};

const DIPAssistant = () => {
  const [query, setQuery] = useState('');
  const [context, setContext] = useState('');
  const [response, setResponse] = useState('');
  const [loading, setLoading] = useState(false);

  const handleAsk = async () => {
    if (!query.trim()) { toast.error('Veuillez saisir une question'); return; }
    setLoading(true);
    try {
      const res = await aiAPI.assistant({ query, context: context || 'Assistant général' });
      setResponse(res.data.response);
    } catch (error) {
      console.error('AI Error:', error);
      toast.error("Erreur lors de la consultation de l'IA");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Contexte (optionnel)</label>
        <Textarea data-testid="ai-context-input" placeholder="Ex: Je travaille sur une crème hydratante..." value={context} onChange={(e) => setContext(e.target.value)} className="min-h-[60px] resize-none text-sm" />
      </div>
      <div className="space-y-2">
        <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Votre question</label>
        <Textarea data-testid="ai-query-input" placeholder="Ex: Quelles alternatives naturelles à la paraben ?" value={query} onChange={(e) => setQuery(e.target.value)} className="min-h-[80px] resize-none text-sm" />
      </div>
      <Button data-testid="btn-ask-ai-dip" onClick={handleAsk} disabled={loading} className="w-full gap-2">
        {loading ? (<><Loader2 className="animate-spin" size={16} />Consultation...</>) : (<><Send size={16} />Demander</>)}
      </Button>
      {response && (
        <Card className="p-4 bg-muted/50 border-accent/20">
          <div className="flex items-start gap-2 mb-3">
            <Sparkles className="text-accent mt-0.5 shrink-0" size={16} />
            <h3 className="font-semibold text-sm text-foreground">Réponse</h3>
          </div>
          <div className="text-sm text-foreground leading-relaxed whitespace-pre-wrap break-words overflow-hidden">{response}</div>
        </Card>
      )}
    </div>
  );
};

const ProductionAssistant = ({ userId }) => {
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversation, setConversation] = useState([]);
  const [pendingActions, setPendingActions] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [quota, setQuota] = useState(null);
  const [boostLoading, setBoostLoading] = useState(false);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);

  const loadQuota = async () => {
    if (!userId) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/quota?user_id=${userId}`);
      if (res.ok) setQuota(await res.json());
    } catch (e) {}
  };
  useEffect(() => { loadQuota(); }, [userId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [conversation]);

  const handleBoost = async () => {
    setBoostLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/boost/create-checkout`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, origin_url: window.location.origin })
      });
      const data = await res.json();
      if (data.url) { window.location.href = data.url; }
    } catch (e) {} finally { setBoostLoading(false); }
  };

  const handleSend = async () => {
    if (!message.trim()) return;
    const userMsg = message.trim();
    setMessage('');
    setConversation(prev => [...prev, { role: 'user', content: userMsg }]);
    setLoading(true);

    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/production-assistant?user_id=${userId || 'demo-user'}&mode=preview`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: userMsg })
      });
      if (res.status === 402) {
        // Quota dépassé : refresh quota state, l'UI passera en mode bloqué
        await loadQuota();
        setConversation(prev => [...prev, { role: 'assistant', content: "Quota IA mensuel atteint. Activez le Boost IA pour continuer.", success: false, actions: [] }]);
        return;
      }
      const data = await res.json();
      // Recharger le quota après chaque appel
      loadQuota();

      if (data.needs_info) {
        setConversation(prev => [...prev, {
          role: 'assistant', content: data.message, success: true, actions: [], isQuestion: true
        }]);
      } else if (data.needs_confirmation && data.proposed_actions?.length > 0) {
        setPendingActions(data.proposed_actions);
        setConversation(prev => [...prev, {
          role: 'assistant', content: data.message, success: true, actions: [],
          proposed: data.proposed_actions
        }]);
      } else if (data.actions_done?.length > 0) {
        setConversation(prev => [...prev, {
          role: 'assistant', content: data.message, success: data.success, actions: data.actions_done
        }]);
        if (data.success) toast.success(`${data.actions_done.length} action(s) effectuée(s)`);
      } else {
        setConversation(prev => [...prev, {
          role: 'assistant', content: data.message, success: data.success, actions: []
        }]);
      }
    } catch (error) {
      setConversation(prev => [...prev, { role: 'assistant', content: "Erreur de communication.", success: false, actions: [] }]);
      toast.error("Erreur de l'assistant");
    } finally { setLoading(false); }
  };

  const handleConfirm = async () => {
    if (!pendingActions) return;
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/execute-actions?user_id=${userId || 'demo-user'}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ proposed_actions: pendingActions })
      });
      const data = await res.json();
      setConversation(prev => [...prev, {
        role: 'assistant', content: data.message, success: data.success, actions: data.actions_done || []
      }]);
      if (data.success && data.actions_done?.length > 0) {
        toast.success(`${data.actions_done.length} action(s) exécutée(s)`);
      }
    } catch (e) {
      toast.error("Erreur lors de l'exécution");
    } finally { setPendingActions(null); setLoading(false); }
  };

  const handleCancel = () => {
    setPendingActions(null);
    setConversation(prev => [...prev, { role: 'assistant', content: "Actions annulées.", success: true, actions: [] }]);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setUploadingDoc(true);
    setConversation(prev => [...prev, { role: 'user', content: `[Document joint : ${file.name}]`, isFile: true }]);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('question', message.trim() || '');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/analyze-document?user_id=${userId || 'demo-user'}`, {
        method: 'POST', body: formData
      });
      const data = await res.json();
      setConversation(prev => [...prev, {
        role: 'assistant', content: data.message, success: data.success, actions: [], isDocAnalysis: true, filename: data.filename
      }]);
      if (data.success) toast.success('Document analysé');
    } catch (err) {
      setConversation(prev => [...prev, { role: 'assistant', content: "Erreur d'analyse du document.", success: false, actions: [] }]);
      toast.error("Erreur d'analyse");
    } finally { setUploadingDoc(false); setMessage(''); }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const examples = [
    "Ajoute du beurre de karité, 1kg à 25€, fournisseur Aroma-Zone",
    "Crée 5 matières : huile de coco, cire d'abeille, vitamine E, beurre de cacao, acide citrique",
    "Mets le stock de beurre de karité à 500g",
    "Crée un baume à lèvres à 8€ public et 5€ pro",
    "Planifie 50 lots de Baume à lèvres au prévisionnel",
  ];

  return (
    <div className="flex flex-col h-full">
      {/* Messages area */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-3 pb-3 min-h-0" style={{ maxHeight: 'calc(100vh - 320px)' }}>
        {conversation.length === 0 ? (
          <div className="space-y-4 py-4">
            <div className="text-center">
              <Factory className="mx-auto mb-3 text-muted-foreground" size={32} />
              <p className="text-sm text-muted-foreground mb-1">Assistant Production IA</p>
              <p className="text-xs text-muted-foreground/70">Donnez-moi un ordre et j'exécute !</p>
            </div>
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground px-1">Exemples</p>
              {examples.map((ex, i) => (
                <button
                  key={i}
                  data-testid={`example-btn-${i}`}
                  onClick={() => setMessage(ex)}
                  className="w-full text-left text-xs px-3 py-2.5 rounded-lg border border-border hover:bg-muted/50 transition-colors text-muted-foreground hover:text-foreground"
                >
                  {ex}
                </button>
              ))}
            </div>
          </div>
        ) : (
          conversation.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[90%] rounded-xl px-3.5 py-2.5 text-sm ${
                  msg.role === 'user'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted border border-border'
                }`}
              >
                {msg.isFile && (
                  <div className="flex items-center gap-1.5 mb-1 text-xs opacity-80">
                    <FileText size={13} /> Document joint
                  </div>
                )}
                {msg.isDocAnalysis && (
                  <div className="flex items-center gap-1.5 mb-2 text-xs font-medium text-primary">
                    <FileText size={13} /> Analyse de {msg.filename}
                  </div>
                )}
                <p className="whitespace-pre-wrap break-words leading-relaxed">{msg.content}</p>

                {/* Actions proposées (en attente de confirmation) */}
                {msg.proposed && msg.proposed.length > 0 && (
                  <div className="mt-2.5 space-y-1.5 border-t border-border/50 pt-2.5">
                    <p className="text-xs font-semibold flex items-center gap-1.5 mb-1 text-amber-600">
                      <AlertCircle size={13} />
                      Actions proposées :
                    </p>
                    {msg.proposed.map((prop, j) => {
                      const config = ACTION_ICON_MAP[prop.type] || ACTION_ICON_MAP.error;
                      const Icon = config.icon;
                      return (
                        <div key={j} className={`flex items-center gap-2 text-xs px-2.5 py-1.5 rounded-md ${config.color}`}>
                          <Icon size={13} className="shrink-0" />
                          <span className="flex-1">{prop.description}</span>
                        </div>
                      );
                    })}
                    {pendingActions && i === conversation.length - 1 && (
                      <div className="flex gap-2 mt-2">
                        <Button data-testid="btn-confirm-actions" size="sm" className="gap-1.5 flex-1" onClick={handleConfirm} disabled={loading}>
                          {loading ? <Loader2 className="animate-spin" size={14} /> : <Check size={14} />}
                          Confirmer
                        </Button>
                        <Button data-testid="btn-cancel-actions" size="sm" variant="outline" className="gap-1.5 flex-1" onClick={handleCancel}>
                          <X size={14} /> Annuler
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Actions effectuées */}
                {msg.actions && msg.actions.length > 0 && (
                  <div className="mt-2.5 space-y-1.5 border-t border-border/50 pt-2.5">
                    <p className="text-xs font-semibold flex items-center gap-1.5 mb-1">
                      <CheckCircle2 size={13} className="text-green-500" />
                      Actions effectuées :
                    </p>
                    {msg.actions.map((action, j) => (
                      <ActionBadge key={j} action={action} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-muted border border-border rounded-xl px-4 py-3">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="animate-spin" size={14} />
                <span>Traitement en cours...</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Quota warning (90%+) */}
      {quota && quota.warning && (
        <div className="px-3 py-2 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-800 flex items-start gap-2 mb-2" data-testid="ai-quota-warning">
          <span className="font-bold">⚠️</span>
          <span>Attention, votre consommation d'IA est très élevée ce mois-ci. Vous approchez de la limite de maintenance.</span>
        </div>
      )}

      {/* Free plan banner */}
      {quota && quota.plan === 'free' && !quota.blocked && (
        <div className="px-3 py-2 bg-blue-50 border border-blue-200 rounded-md text-xs text-blue-900 flex items-center gap-2 mb-2" data-testid="ai-free-banner">
          <Sparkles size={14} className="text-blue-700 shrink-0" />
          <span>Plan gratuit — pour profiter davantage de l'assistant IA, <a href="/subscription" className="underline font-semibold hover:no-underline">passez au Premium</a>.</span>
        </div>
      )}

      {/* Input area */}
      <div className="border-t border-border pt-3 mt-auto">
        {quota && quota.blocked ? (
          quota.plan === 'free' ? (
            <div className="text-center py-4 px-3 bg-gradient-to-br from-[#205F79]/5 to-amber-50 border border-[#205F79]/20 rounded-lg" data-testid="ai-free-blocked">
              <Sparkles size={22} className="mx-auto text-[#205F79] mb-2" />
              <p className="text-sm font-semibold text-gray-900 mb-1">Limite mensuelle atteinte</p>
              <p className="text-xs text-gray-600 mb-3">Pour profiter davantage de l'assistant IA, passez au Premium.</p>
              <Button className="bg-[#205F79] hover:bg-[#184a5e] gap-2 w-full" onClick={() => window.location.href = '/subscription'} data-testid="btn-go-premium">
                <Crown size={14} /> Passer au Premium
              </Button>
            </div>
          ) : (
            <div className="text-center py-4 px-3 bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-300 rounded-lg" data-testid="ai-premium-blocked">
              <Zap size={22} className="mx-auto text-amber-600 mb-2" />
              <p className="text-sm font-semibold text-gray-900 mb-1">Limite mensuelle atteinte</p>
              <p className="text-xs text-gray-600 mb-3">Activez le Boost IA pour continuer immédiatement.</p>
              <Button className="bg-amber-500 hover:bg-amber-600 text-white gap-2 w-full" onClick={handleBoost} disabled={boostLoading} data-testid="btn-ai-boost">
                {boostLoading ? <Loader2 className="animate-spin" size={14} /> : <Zap size={14} />}
                Activer le Boost IA (5€)
              </Button>
            </div>
          )
        ) : (
          <>
            <div className="flex gap-2 items-end">
              <input ref={fileInputRef} type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.txt,.csv,.doc,.docx"
                onChange={handleFileUpload} />
              <Button data-testid="btn-upload-doc" variant="outline" size="icon" className="shrink-0 h-[44px] w-[44px]"
                onClick={() => fileInputRef.current?.click()} disabled={uploadingDoc || loading}
                title="Joindre un document">
                {uploadingDoc ? <Loader2 className="animate-spin" size={18} /> : <Paperclip size={18} />}
              </Button>
              <Textarea
                data-testid="prod-ai-input"
                placeholder={pendingActions ? "Confirmez ou annulez les actions..." : "Ex: Ajoute 3 matières premières..."}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                className="resize-none text-sm min-h-[44px] max-h-[100px]"
                rows={1}
                disabled={!!pendingActions}
              />
              <Button data-testid="btn-send-prod-ai" onClick={handleSend} disabled={loading || !message.trim() || !!pendingActions} size="icon" className="shrink-0 h-[44px] w-[44px]">
                {loading ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground/60 mt-1.5 text-center">Entrée pour envoyer · Shift+Entrée saut de ligne · Trombone pour joindre un document</p>
          </>
        )}
      </div>
    </div>
  );
};

const AIAssistant = ({ user }) => {
  return (
    <Tabs defaultValue="production" className="h-full flex flex-col mt-4">
      <TabsList className="grid w-full grid-cols-2 shrink-0">
        <TabsTrigger value="production" data-testid="tab-ai-prod" className="gap-1.5">
          <Factory size={14} />
          Production
        </TabsTrigger>
        <TabsTrigger value="dip" data-testid="tab-ai-dip" className="gap-1.5">
          <Beaker size={14} />
          DIP
        </TabsTrigger>
      </TabsList>

      <TabsContent value="production" className="flex-1 min-h-0 mt-3">
        <ProductionAssistant userId={user?.id} />
      </TabsContent>

      <TabsContent value="dip" className="flex-1 min-h-0 mt-3">
        <DIPAssistant />
      </TabsContent>
    </Tabs>
  );
};

export default AIAssistant;
